import { IKImage } from 'imagekitio-react'
import React, { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MyContext } from '../contexts/MyContext'
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from 'react-responsive-carousel';
import { FaExternalLinkAlt } from "react-icons/fa";
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';

const ProjectDetails = () => {
    const navigate = useNavigate();
    let projectId = parseInt(useParams().projectId)
    const {projects} = useContext(MyContext)
    const { isDark } = useTheme();
    const fetchedProject = projects.find(a => a.id===projectId)

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className='flex-1 py-12'
        >
            <div className='max-w-4xl mx-auto space-y-6'>
                {/* Header Card - Title & Date */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className={`rounded-lg border p-6 ${
                        isDark
                            ? 'bg-dark-bg-card border-dark-border'
                            : 'bg-light-bg-card border-light-border shadow-sm'
                    }`}
                >
                    {/* Date */}
                    <p className={`text-sm font-mono mb-3 ${
                        isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                    }`}>
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            //
                        </span>
                        {' '}Date: {fetchedProject?.date}
                    </p>

                    {/* Project Title */}
                    <h1 className={`font-mono font-bold text-3xl md:text-4xl lg:text-5xl ${
                        isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                    }`}>
                        <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
                            $
                        </span>
                        {' '}{fetchedProject?.name}
                    </h1>
                </motion.div>

                {/* Image Carousel Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className={`rounded-lg border overflow-hidden ${
                        isDark ? 'border-dark-border' : 'border-light-border shadow-lg'
                    }`}
                >
                    <Carousel
                        autoPlay
                        infiniteLoop
                        showThumbs={false}
                        showStatus={false}
                        className="project-carousel"
                    >
                        {fetchedProject?.image?.map((item, index) => {
                            return (
                                <div key={index} className="aspect-video">
                                    <IKImage
                                        path={item}
                                        transformation={[{ radius:10 }]}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )
                        })}
                    </Carousel>
                </motion.div>

                {/* Description Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className={`rounded-lg border p-6 ${
                        isDark
                            ? 'bg-dark-bg-card border-dark-border'
                            : 'bg-light-bg-card border-light-border shadow-sm'
                    }`}
                >
                    <h3 className={`font-mono font-semibold text-lg mb-3 ${
                        isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                    }`}>
                        <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                            &gt;
                        </span>
                        {' '}Description
                    </h3>
                    <p className={`font-mono text-sm md:text-base leading-relaxed ${
                        isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                    }`}>
                        {fetchedProject?.description}
                    </p>
                </motion.div>

                {/* Dependencies Card */}
                {fetchedProject?.dependencies && fetchedProject.dependencies.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className={`rounded-lg border p-6 ${
                            isDark
                                ? 'bg-dark-bg-card border-dark-border'
                                : 'bg-light-bg-card border-light-border shadow-sm'
                        }`}
                    >
                        <h3 className={`font-mono font-semibold text-lg mb-3 ${
                            isDark ? 'text-dark-text-primary' : 'text-light-text-primary'
                        }`}>
                            <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
                                &gt;
                            </span>
                            {' '}Dependencies
                        </h3>
                        <ul className={`list-disc ml-6 font-mono text-sm space-y-1 ${
                            isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                        }`}>
                            {fetchedProject?.dependencies?.map((item, index) => {
                                return (
                                    <li key={index}>{item}</li>
                                )
                            })}
                        </ul>
                    </motion.div>
                )}

                {/* Links & Tags Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className={`rounded-lg border p-6 ${
                        isDark
                            ? 'bg-dark-bg-card border-dark-border'
                            : 'bg-light-bg-card border-light-border shadow-sm'
                    }`}
                >
                    {/* Link */}
                    <div className="mb-6">
                        <a
                            href={`${fetchedProject?.githubLink || fetchedProject?.website_link}`}
                            target="_blank"
                            rel="noreferrer"
                            className={`inline-flex items-center gap-2 font-mono text-sm px-4 py-2 rounded-lg border transition-all ${
                                isDark
                                    ? 'text-dark-brand-primary border-dark-brand-primary hover:bg-dark-brand-primary hover:text-dark-bg-primary'
                                    : 'text-light-brand-primary border-light-brand-primary hover:bg-light-brand-primary hover:text-white'
                            }`}
                        >
                            View Source Code
                            <FaExternalLinkAlt className='text-xs'/>
                        </a>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`font-mono text-sm ${
                            isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                        }`}>
                            Tags:
                        </span>
                        {fetchedProject?.category.map((item, index) => (
                            <motion.button
                                key={index}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate("/projects", {state: { tab: item}})}
                                className={`font-mono text-xs px-3 py-1 rounded border transition-all ${
                                    isDark
                                        ? 'text-dark-brand-secondary border-dark-border bg-dark-bg-tertiary hover:border-dark-brand-secondary'
                                        : 'text-light-brand-secondary border-light-border bg-light-bg-tertiary hover:border-light-brand-secondary'
                                }`}
                            >
                                #{item}
                            </motion.button>
                        ))}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    )
}

export default ProjectDetails
